import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, Clock, MapPin, Download, Rss, Copy } from "lucide-react";
import { toast } from "sonner";

export default function TimelineTab({ eventId, members, canEdit }) {
  const [blocks, setBlocks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ time: "14:00", title: "", description: "", duration_min: 30, vendor_member_id: "", location: "" });

  const vendors = members.filter(m => m.role === "vendor" || m.role === "coordinator");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/events/${eventId}/timeline`); setBlocks(data); }
    catch (e) { toast.error("Could not load timeline"); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.title || !form.time) { toast.error("Time and title required"); return; }
    try {
      await api.post(`/events/${eventId}/timeline`, { ...form, duration_min: Number(form.duration_min) || 30, vendor_member_id: form.vendor_member_id || null });
      setOpen(false);
      setForm({ time: "14:00", title: "", description: "", duration_min: 30, vendor_member_id: "", location: "" });
      load();
      toast.success("Timeline updated");
    } catch (e) { toast.error("Could not add"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this block?")) return;
    try { await api.delete(`/events/${eventId}/timeline/${id}`); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">MOD.TIMELINE / DAY_OF</div>
          <h2 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none">
            <span className="font-mono tabular-nums">{blocks.length.toString().padStart(2, "0")}</span> moments
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <IcalActions eventId={eventId} />
          {canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-timeline-btn" className="rounded-sm bg-app hover:bg-[#000000]">
                  <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add moment
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-surface rounded-sm max-w-lg">
              <DialogHeader><DialogTitle className="font-serif text-2xl text-app">Add to timeline</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <F label="Start time"><Input data-testid="timeline-time-input" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></F>
                  <F label="Duration (min)"><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></F>
                </div>
                <F label="Title"><Input data-testid="timeline-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Ceremony processional" /></F>
                <F label="Details"><Textarea rows={2} className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
                <F label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Garden lawn" /></F>
                <F label="Responsible">
                  <Select value={form.vendor_member_id || "none"} onValueChange={(v) => setForm({ ...form, vendor_member_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Planner</SelectItem>
                      {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name} · {v.vendor_category || v.role}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="timeline-submit" onClick={add} className="rounded-sm bg-app hover:bg-[#000000]">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {blocks.length === 0 ? (
        <div className="bevel border-hair rounded-sm p-10 text-center text-muted font-light">
          Build the hour-by-hour schedule for the big day.
        </div>
      ) : (
        <div className="bevel border-hair rounded-sm">
          {blocks.map((b, i) => {
            const vendor = members.find(m => m.id === b.vendor_member_id);
            return (
              <div key={b.id} data-testid={`timeline-row-${b.id}`} className="grid grid-cols-[110px_1fr_auto] gap-6 p-6 border-b border-hair/60 last:border-b-0 hover:bg-canvas/40 group">
                <div className="text-right">
                  <div className="font-serif text-2xl text-app">{formatTime(b.time)}</div>
                  <div className="eyebrow mt-1">{b.duration_min}m</div>
                </div>
                <div className="border-l border-hair pl-6 relative">
                  <span className="absolute -left-1.5 top-2 w-3 h-3 rounded-full bg-app" />
                  <div className="font-medium text-app">{b.title}</div>
                  {b.description && <div className="text-sm text-muted font-light mt-1">{b.description}</div>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted">
                    {vendor && <span>→ {vendor.name}</span>}
                    {b.location && <span className="flex items-center gap-1"><MapPin size={11} strokeWidth={1.5}/>{b.location}</span>}
                  </div>
                </div>
                {canEdit && (
                  <button onClick={() => del(b.id)} data-testid={`delete-timeline-${b.id}`} className="text-muted hover:text-[#EF4444] opacity-0 group-hover:opacity-100 transition-opacity self-start">
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2,"0")} ${ampm}`;
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}

export function IcalActions({ eventId, memberId }) {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const url = memberId ? `/events/${eventId}/members/${memberId}/timeline.ics` : `/events/${eventId}/timeline.ics`;
      const resp = await api.get(url, { responseType: "blob" });
      const objUrl = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = memberId ? `vendor-schedule.ics` : `event-timeline.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch (e) { toast.error("Download failed"); }
    finally { setBusy(false); }
  };
  const copySubscribe = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/ics-token`);
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = memberId
        ? `${base}/api/public/calendar/${eventId}/vendor/${memberId}.ics?token=${data.ics_token}`
        : `${base}/api/public/calendar/${eventId}.ics?token=${data.ics_token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Subscribe URL copied — paste into Google or Apple Calendar");
    } catch (e) { toast.error("Could not get link"); }
  };
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={download}
        disabled={busy}
        data-testid={memberId ? `download-vendor-ics-${memberId}` : "download-ics"}
        className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border border-hair hover:border-[#0A0A0A] hover:bg-canvas transition-colors"
        title="Download .ics"
      >
        <Download size={13} strokeWidth={1.5} /> .ics
      </button>
      <button
        onClick={copySubscribe}
        data-testid={memberId ? `subscribe-vendor-ics-${memberId}` : "subscribe-ics"}
        className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border border-hair hover:border-[#0A0A0A] hover:bg-canvas transition-colors"
        title="Copy calendar-subscribe URL"
      >
        <Rss size={13} strokeWidth={1.5} /> Subscribe
      </button>
    </div>
  );
}
